/* eslint-disable @wyz/module/no-relative-import */
/* eslint-disable no-mixed-operators */

import { IContainerSearchResult } from '~/purchase/types';
import './fill-rate-chart.css';

const COLORS = {
  FILL_COLOR: 'var(--primary-color)',
  OVERFLOW_COLOR: 'var(--danger-text)',
  PLACEHOLDER_COLOR: 'var(--neutral-grey-5)',

  BORDER_LIGHT: 'var(--neutral-grey-5)',

  TEXT_PRIMARY: 'var(--title-text-color)',
} as const;

interface ContainerFillRateChartProps {
  data: IContainerSearchResult['summary']['fillRates'];
}

const VerticalChartLayout = ({ data }: ContainerFillRateChartProps) => {
  const chartWidth = '100%';
  const chartHeight = 350;
  const margin = { top: 140, right: 0, bottom: 0, left: 40 }; // Increased top margin to accommodate overflow elements
  const viewBoxWidth = 1600; // Base width for calculations
  const barWidth = (viewBoxWidth - margin.left - margin.right) / data.length;
  const maxFillRate = 100;

  return (
    <div>
      <svg
        width={chartWidth}
        height={chartHeight}
        viewBox={`0 0 ${viewBoxWidth} ${chartHeight}`}
        preserveAspectRatio='xMidYMid meet'
        style={{
          width: '100%',
        }}
      >
        {[0, 25, 50, 75, 100].map((value) => {
          const y =
            margin.top +
            (chartHeight - margin.top - margin.bottom) *
              (1 - value / maxFillRate);
          return (
            <g key={value}>
              <line
                x1={margin.left}
                y1={y}
                x2={viewBoxWidth - margin.right}
                y2={y}
                stroke={COLORS.BORDER_LIGHT}
                strokeWidth={value === 0 ? 2 : 1}
                strokeDasharray={3}
              />
              <text
                x={margin.left - 10}
                y={y + 4}
                textAnchor='end'
                fontSize='12'
                opacity={value === 25 || value === 75 ? 0.5 : 1}
              >
                {`${value}%`}
              </text>
            </g>
          );
        })}

        {data.map((rateItem, index) => {
          const rate = rateItem.ratio * 100;
          const x = margin.left + index * barWidth + barWidth * 0.2;
          const barHeight =
            (chartHeight - margin.top - margin.bottom) *
            (Math.min(rate, 125) / maxFillRate);
          const y = chartHeight - margin.bottom - barHeight;
          const actualBarWidth = barWidth * 0.5;

          const fillColor = rateItem.overflow
            ? COLORS.OVERFLOW_COLOR
            : COLORS.FILL_COLOR;
          const strokeColor =
            rate > 0 ? COLORS.FILL_COLOR : COLORS.BORDER_LIGHT;

          return (
            <g key={index}>
              <rect
                x={x}
                y={margin.top + (chartHeight - margin.top - margin.bottom) * 0}
                width={actualBarWidth}
                height={chartHeight - margin.top - margin.bottom}
                fill={COLORS.PLACEHOLDER_COLOR}
                stroke={COLORS.PLACEHOLDER_COLOR}
                strokeWidth={1}
                opacity={0.5}
              />

              {rate > 0 && (
                <rect
                  className='fill-rate-bar chart-bar-enter'
                  x={x}
                  y={y}
                  width={actualBarWidth}
                  height={barHeight}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={1}
                />
              )}
              <text
                className='fill-rate-text'
                x={x + actualBarWidth / 2}
                y={margin.top - (rate > 100 ? 105 : 10)}
                textAnchor='middle'
                fontSize={16}
                fontWeight={600}
                fill={COLORS.FILL_COLOR}
                style={{ zIndex: 1 }}
              >
                {index + 1}
              </text>
              {rateItem.overflow && (
                <text
                  className='fill-rate-text'
                  x={x + actualBarWidth / 2}
                  y={80}
                  textAnchor='middle'
                  fontSize='20'
                  fontWeight={700}
                  fill={COLORS.OVERFLOW_COLOR}
                  style={{ zIndex: 1 }}
                >
                  {`${rate.toFixed(1)}%`}
                </text>
              )}

              {rate > 100 && (
                <g
                  style={{
                    pointerEvents: 'none',
                  }}
                  transform={`translate(${x + actualBarWidth / 2 - 10}, 40) scale(1.5)`}
                >
                  <path
                    d='M6.94612 1.76373C6.96131 1.75529 6.9803 1.75 7.00135 1.75C7.02241 1.75 7.04139 1.75529 7.05659 1.76373C7.0697 1.77103 7.08705 1.78435 7.10446 1.81398L13.1043 12.0223C13.1355 12.0754 13.1349 12.131 13.1062 12.1827C13.0918 12.2087 13.0738 12.2258 13.0583 12.2354C13.0454 12.2434 13.0288 12.25 13.0012 12.25H1.00152C0.973904 12.25 0.957288 12.2434 0.944405 12.2354C0.928925 12.2258 0.910924 12.2087 0.896532 12.1827C0.867827 12.131 0.867199 12.0754 0.898404 12.0223L6.89824 1.81398C6.91565 1.78435 6.933 1.77103 6.94612 1.76373ZM7.85882 1.37062C7.47043 0.709794 6.53227 0.709794 6.14388 1.37062L0.144048 11.579C-0.255856 12.2594 0.224244 13.125 1.00152 13.125H13.0012C13.7785 13.125 14.2586 12.2594 13.8587 11.579L7.85882 1.37062Z'
                    fill={COLORS.OVERFLOW_COLOR}
                  />
                  <path
                    d='M6.12635 10.5C6.12635 10.0168 6.5181 9.625 7.00135 9.625C7.4846 9.625 7.87635 10.0168 7.87635 10.5C7.87635 10.9832 7.4846 11.375 7.00135 11.375C6.5181 11.375 6.12635 10.9832 6.12635 10.5Z'
                    fill={COLORS.OVERFLOW_COLOR}
                  />
                  <path
                    d='M6.21207 5.24566C6.16545 4.7795 6.53152 4.375 7 4.375C7.46848 4.375 7.83455 4.7795 7.78793 5.24566L7.48103 8.31467C7.45632 8.5618 7.24836 8.75 7 8.75C6.75164 8.75 6.54368 8.5618 6.51897 8.31467L6.21207 5.24566Z'
                    fill={COLORS.OVERFLOW_COLOR}
                  />
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const ContainerFillRateChart = ({ data }: ContainerFillRateChartProps) => {
  return <VerticalChartLayout data={data} />;
};

export default ContainerFillRateChart;
